/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { FaPlay } from 'react-icons/fa';
import { GoHeart, GoHeartFill } from 'react-icons/go';
import { RiPlayList2Fill } from 'react-icons/ri';
import ReactPlayer from 'react-player';
import { CONTENT_TYPES, CONTENT_SECTIONS } from '../../../constants';
import Playlistimg from '../../../../../assets/Resources/playlistimg.svg';
import Videos from '../../../../../assets/SvgIcons/resources/videos.svg';
import ScheduleTimeIcon from '../../../../../assets/Resources/scheduleTimeIcon.svg';
import { useFavorite } from '../useFavorite';
import { getFileTypeIconUrl } from '../../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Creation/utils';
import useResourceHelper from '../../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Reusable/ContentCards/useResourceHelper';
import { dateFormatDDMMMYYYYHHMM } from '../../../../../libs/HelperContent';

interface ThumbnailViewProps {
	type: string;
	contentData: any;
	section?: string;
	fileUrl?: string;
	watermarkTxt?: string;
	isAdminView?: boolean;
	// hovered?: boolean;
	// isDraft?: boolean;
	// isPublished?: boolean;
	// isScheduled?: boolean;
	from?: string;
	contentStatus?: string;
}
const getContentType = (type: string): 'video' | 'document' | 'image' | 'article' | 'podcast' | 'playlist' =>
{
	switch (type)
	{
		case CONTENT_TYPES.VIDEOS:
			return 'video';
		case CONTENT_TYPES.DOCUMENTS:
			return 'document';
		case CONTENT_TYPES.IMAGES:
			return 'image';
		case CONTENT_TYPES.ARTICLES:
			return 'article';
		case CONTENT_TYPES.PODCASTS:
			return 'podcast';
		case CONTENT_TYPES.PLAYLISTS:
			return 'playlist';
		default:
			return 'document';
	}
};

const ThumbnailView: React.FC<ThumbnailViewProps> = ({
	type,
	contentData,
	section,
	fileUrl,
	watermarkTxt,
	isAdminView = true,
	from,
	contentStatus
}) =>
{
	const [ progress, setProgress ] = useState<number>(30);

	const [ calculatedDuration, setCalculatedDuration ] = useState<number>(0);
	const [ shouldLoadDuration, setShouldLoadDuration ] = useState<boolean>(false);
	const [ previewOpen, setPreviewOpen ] = useState<boolean>(false);
	const [ imgError, setImgError ] = useState<boolean>(false);
	const { toggleFavorite, isFavorite, isLoading: isFavoriteLoading } = useFavorite({
		contentType          : getContentType(type),
		contentId            : contentData?._id ?? '',
		initialFavoriteState : Boolean(contentData?.isFavoritedByMe) ?? false
	});
	const { getRandomBackgroundImage } = useResourceHelper();
	const isPublished = contentStatus === 'published';
	const isScheduled = contentStatus === 'scheduled';
	const isDraft = contentStatus === 'draft';
	const isEnabled = contentStatus === 'enabled';
	const isDisabled = contentStatus === 'disabled';
	const isUnpublished = contentStatus === 'unpublished';

	const handleHeart = async (e: React.MouseEvent) =>
	{
		e.stopPropagation();
		if (!isFavoriteLoading)
		{
			await toggleFavorite();
		}
	};

	// Get final duration - use API duration if available, otherwise use calculated
	const getFinalDuration = (): number =>
	{
		const apiDuration = contentData?.videos?.[0]?.duration || contentData?.totalDuration;

		// If API duration exists and is greater than 0, use it
		if (apiDuration && apiDuration > 0)
		{
			return apiDuration;
		}

		// Otherwise use calculated duration
		return calculatedDuration;
	};

	// Format duration for display
	const formatDuration = (seconds?: number): string =>
	{
		if (!seconds || seconds <= 0) return '0:00';

		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const remainingSeconds = Math.floor(seconds % 60);

		// Consistent formatting helper following codebase pattern
		const formatTimeUnit = (value: number): string => String(value).padStart(2, '0');

		if (hours > 0)
		{
			// For videos longer than 1 hour: "H:MM:SS" or "HH:MM:SS"
			// Use consistent 2-digit padding for minutes and seconds when hours are present
			return `${hours}:${formatTimeUnit(minutes)}:${formatTimeUnit(remainingSeconds)}`;
		}
		else
		{
			// For videos under 1 hour: "MM:SS"
			// Use 2-digit padding for consistency with other duration formatters in codebase
			return `${formatTimeUnit(minutes)}:${formatTimeUnit(remainingSeconds)}`;
		}
	};

	// Handle duration from ReactPlayer
	const handleDuration = (duration: number): void =>
	{
		if (duration && duration > 0)
		{
			setCalculatedDuration(Math.floor(duration));
		}
	};

	// const handleClosePreview = (_event?: React.MouseEvent<HTMLElement>): void =>
	// {
	// 	if (_event)
	// 	{
	// 		_event.stopPropagation();
	// 	}
	// 	setPreviewOpen(false);
	// };
	// Check if we're using a default/random image
	const isUsingDefaultImage = useMemo(() =>
	{
		// If there's an image error, we're using random background image
		if (imgError) return true;

		if (type === CONTENT_TYPES.DOCUMENTS) return false;

		// Check if any thumbnail URL exists in content data
		const hasThumbnailUrl = contentData?.thumbnail?.url ||
								contentData?.thumbnailUrl ||
								contentData?.videos?.[0]?.thumbnail?.url ||
								contentData?.thumbnail;

		// If thumbnail URL exists, don't show title overlay
		if (hasThumbnailUrl) return false;

		// For videos, also check if there's a video URL
		if (type === CONTENT_TYPES.VIDEOS && (contentData?.videos?.[0]?.url || contentData?.url))
		{
			return false;
		}

		// For other types, check if fileUrl exists
		if (fileUrl) return false;

		// If no thumbnail URLs found, show title overlay
		return true;
	}, [ type, contentData, fileUrl, imgError ]);

	const getImageUrl = useMemo(() =>
	{
		// Helper function to get random image if thumbnail is null
		const getImageWithFallback = (thumbnail: any) =>
		{
			if (thumbnail?.url)
			{
				return thumbnail.url;
			}

			return getRandomBackgroundImage;
		};

		switch (type)
		{
			case CONTENT_TYPES.DOCUMENTS:
				return getFileTypeIconUrl(contentData?.files?.[0]?.fileType ?? contentData?.fileType);
			case CONTENT_TYPES.PODCASTS:
				return getImageWithFallback(contentData?.coverImage);
			case CONTENT_TYPES.PLAYLISTS:
				return contentData?.thumbnailUrl || getRandomBackgroundImage;
			case CONTENT_TYPES.ARTICLES:
				return contentData?.thumbnail || getRandomBackgroundImage;
			default:
				return fileUrl || getRandomBackgroundImage;
		}
	}, [ type, contentData, fileUrl, getRandomBackgroundImage ]);

	// Check if we need to load duration on component mount
	useEffect(() =>
	{
		if (type === CONTENT_TYPES.VIDEOS)
		{
			const apiDuration = contentData?.videos?.[0]?.duration ||
			contentData?.totalDuration;

			if (!apiDuration || apiDuration === 0)
			{
				setShouldLoadDuration(true);
			}
		}
	}, [ type, contentData ]);

	// Only show progress if from === "continue" and valid watchData
	const shouldShowProgress =
		from === 'continue' &&
		type === CONTENT_TYPES.VIDEOS &&
		contentData?.watchData &&
		typeof contentData?.watchData?.currentTime === 'number' &&
		typeof contentData?.watchData?.duration === 'number' &&
		contentData?.watchData?.duration > 0;

	const progressPercent = shouldShowProgress
		? Math.min(
			100,
			(contentData?.watchData?.currentTime / contentData?.watchData?.duration) * 100
		)
		: 0;

	/**
	 * Returns the status text based on content status
	 * @returns string - 'Published', 'Unpublished', or formatted date for scheduled content
	 */
	const statusText = useMemo(() =>
	{
		// Handle playlist status first
		if (type === CONTENT_TYPES.PLAYLISTS)
		{
			if (isEnabled) return 'Enabled';
			if (isDisabled) return 'Disabled';

			return 'Disabled'; // Default fallback
		}
		// Handle other content types
		if (isPublished)
		{
			return 'Published';
		}
		else if (isUnpublished)
		{
			return 'Unpublished';
		}
		else if (isScheduled)
		{
			// Check if there's a scheduled date in contentData
			const scheduledDate = contentData?.scheduledPublishDate;

			if (scheduledDate)
			{
				// Format the date - could use a library like dayjs/moment here
				try
				{
					const date = new Date(scheduledDate);

					return dateFormatDDMMMYYYYHHMM(date);
				}
				catch (e)
				{
					return 'Scheduled';
				}
			}

			return 'Scheduled';
		}
		else if (isDraft)
		{
			return 'Draft';
		}

		// Default fallback to StatusTxtMsg or empty string
		return '';
	}, [ isPublished, isScheduled, isDraft, contentStatus, type, contentData?.scheduledPublishDate ]);

	return (
		<Box
			sx={{
				position     : 'relative',
				width        : 'var(--percent100)',
				aspectRatio  : '16 / 9',
				overflow     : 'hidden',
				borderRadius : '13px'
			}}
		>
			{type === CONTENT_TYPES.VIDEOS
				? (
					<Box
						sx={{
							position                     : 'relative',
							width                        : '100%',
							height                       : '100%',
							backgroundColor              : (!contentData?.videos?.[0]?.url && !contentData?.videos?.[0]?.thumbnail?.url) ? '#000' : 'transparent',
							borderRadius                 : '13px',
							'& .react-player__play-icon' : {
								display : 'none !important'
							},
							'& .react-player__preview' : {
								borderRadius : '13px !important'
							}
						}}
					>
						<ReactPlayer
							url={contentData?.videos?.[0]?.url ?? contentData?.url}
							width='100%'
							height='100%'
							controls={false}
							playing={false}
							light={contentData?.videos?.[0]?.thumbnail?.url ?? contentData?.thumbnail}
							playsinline
							config={{
								youtube : {
									playerVars : {
										controls       : 0,
										modestbranding : 1,
										rel            : 0,
										showinfo       : 0,
										iv_load_policy : 3,
										fs             : 0,
										cc_load_policy : 0,
										disablekb      : 1,
										autohide       : 1
									}
								},
								file : {
									attributes : {
										style : {
											width     : '100%',
											height    : '100%',
											objectFit : 'cover'
										}
									}
								}
							}}
							onError={(_error) =>
							{
								console.error('Video load error:', _error);
							}}
							onDuration={handleDuration}
							style={{
								borderRadius : '13px',
								aspectRatio  : '16 / 9'
							}}
							className='custom-react-player'
						/>

						{/* Hidden ReactPlayer for duration calculation only */}
						{type === CONTENT_TYPES.VIDEOS && shouldLoadDuration && (
							<ReactPlayer
								url={contentData?.videos?.[0]?.url || contentData?.url}
								width='0'
								height='0'
								style={{
									position   : 'absolute',
									top        : '-9999px',
									left       : '-9999px',
									visibility : 'hidden'
								}}
								playing={false}
								controls={false}
								muted
								onDuration={(duration) =>
								{
									handleDuration(duration);
									setShouldLoadDuration(false); // Stop loading once we get duration
								}}
								onError={() =>
								{
									setShouldLoadDuration(false); // Stop trying if error
								}}
							/>
						)}
					</Box>
				)
				: type === CONTENT_TYPES.DOCUMENTS
					? (
						<Box
							position='relative'
							width='100%'
							height='100%'
							sx={{
								borderRadius    : '13px',
								aspectRatio     : '16 / 9',
								backgroundColor : '#F5F5F5',
								cursor          : 'pointer',
								display         : 'flex',
								alignItems      : 'center',
								justifyContent  : 'center'
							}}
						>
							<Box
								component='img'
								src={getFileTypeIconUrl(contentData?.files?.[0]?.fileType ?? contentData?.fileType)}
								alt={`${contentData?.files?.[0]?.fileType ?? contentData?.fileType ?? 'document'} icon`}
								sx={{
									width     : 65,
									height    : 65,
									objectFit : 'contain'
								}}
							/>
						</Box>
					)
					: (
						<Box
							component='img'
							src={imgError ? getRandomBackgroundImage : getImageUrl}
							width='100%'
							height='100%'
							alt={''}
							onError={() => setImgError(true)}
							sx={{
								borderRadius    : '13px',
								backgroundColor : 'gray',
								transition      : 'transform 0.3s ease-in-out',
								'&:hover'       : { transform: 'scale(1.1)' }
							}}
						/>
					)}

			{/* Play button overlay for videos */}
			{type === CONTENT_TYPES.VIDEOS && (
				<Box
					bgcolor='rgba(0, 0, 0, 0.6)'
					height='var(--pixel40)'
					width='var(--pixel40)'
					borderRadius='var(--percent50)'
					position='absolute'
					left='50%'
					top='50%'
					sx={{
						transform      : 'translate(-50%, -50%)',
						cursor         : 'pointer',
						display        : 'flex',
						alignItems     : 'center',
						justifyContent : 'center',
						transition     : 'all 0.2s ease',
						zIndex         : 10,
						'&:hover'      : {
							backgroundColor : 'rgba(0, 0, 0, 0.8)',
							transform       : 'translate(-50%, -50%) scale(1.1)'
						}
					}}
					color='#fff'
				>
					<FaPlay fontSize={'var(--pixel14)'} />
				</Box>
			)}

			{/* Content title overlay for default images */}
			{isUsingDefaultImage && (contentData?.title || (type === CONTENT_TYPES.PLAYLISTS && contentData?.name)) && (
				<Box
					position='absolute'
					left='50%'
					top='50%'
					sx={{
						transform : 'translate(-50%, -50%)',
						zIndex    : 10,
						width     : '90%'
					}}
				>
					<Typography
						color='white'
						fontWeight={500}
						sx={{
							display         : '-webkit-box',
							WebkitBoxOrient : 'vertical',
							WebkitLineClamp : 3,
							overflow        : 'hidden',
							textOverflow    : 'ellipsis',
							fontSize        : '22px',
							color           : '#256EA6',
							lineHeight      : 1.5,
							opacity         : 0.5
						}}
					>
						{type === CONTENT_TYPES.PLAYLISTS ? contentData?.name : contentData?.title}
					</Typography>
				</Box>
			)}

			{(type === CONTENT_TYPES.PODCASTS ||
				type === CONTENT_TYPES.VIDEOS ||
				type === CONTENT_TYPES.DOCUMENTS ||
				type === CONTENT_TYPES.PLAYLISTS ||
				type === CONTENT_TYPES.AUDIOS) && (
				<Box
					bgcolor='#555555'
					height='var(--pixel20)'
					px={'13px'}
					borderRadius='20px'
					position='absolute'
					right='8px'
					color='#fff'
					bottom='8px'
					display='flex'
					alignItems='center'
					justifyContent='center'
					sx={{ zIndex: 10 }}
				>
					{type === CONTENT_TYPES.PLAYLISTS ||
							(section === CONTENT_SECTIONS.PLAYLISTS &&
								type === CONTENT_TYPES.PODCASTS)
						? (
							<Typography
								variant='caption'
								display={'flex'}
								alignItems={'center'}
							>
								<RiPlayList2Fill />
									&nbsp;{contentData?.contentCount || 0} Contents
							</Typography>
						)
						: window.location.pathname.split('/')[3] === 'playlist' || window.location.pathname === '/resource/playlist'
							? (
								<Typography
									variant='caption'
									display={'flex'}
									alignItems={'center'}
								>
									<RiPlayList2Fill />
										&nbsp;{contentData?.contentCount || 0} Contents
								</Typography>
							)
							: type === CONTENT_TYPES.DOCUMENTS
								? (
									<Typography variant='caption'>{contentData?.files?.[0]?.fileType?.toUpperCase() ?? contentData?.fileType?.toUpperCase() ?? 'PDF'}</Typography>
								)
								: type === CONTENT_TYPES.PODCASTS
									? (
										<Typography
											variant='caption'
											display={'flex'}
											alignItems={'center'}
										>
											<RiPlayList2Fill />
												&nbsp;{contentData?.episodes?.length || 0} Audios
										</Typography>
									)
									: (
										<Typography variant='caption'>
											{formatDuration(getFinalDuration())}
										</Typography>
									)}
				</Box>
			)}
			{!isAdminView && (
				<Box
					className={`heart-button ${isFavorite ? 'favorited' : ''}`}
					height='var(--pixel48)'
					width='var(--pixel48)'
					position='absolute'
					right={0}
					borderRadius='0 13px 0 40px'
					top={0}
					color='#fff'
					display='flex'
					alignItems='center'
					justifyContent='center'
					onClick={handleHeart}
					sx={{
						cursor          : 'pointer',
						backgroundColor : 'rgba(255,255,255,0.25)',
						backdropFilter  : 'blur(8px)',
						opacity         : isFavoriteLoading ? 0.7 : 1,
						pointerEvents   : isFavoriteLoading ? 'none' : 'auto'
					}}
				>
					{isFavorite
						? <GoHeartFill fontSize='var(--pixel22)' color='#24A4C7' />
						: <GoHeart fontSize='var(--pixel22)' color='var(--whiteColor)' />
					}
				</Box>
			)}

			{shouldShowProgress && (
				<Box
					sx={{
						position     : 'absolute',
						left         : 0,
						bottom       : 0,
						height       : '4px',
						width        : `${progressPercent}%`,
						background   : '#F24B59',
						borderRadius : '0 0 13px 13px',
						zIndex       : 20,
						transition   : 'width 0.3s'
					}}
				/>
			)}

			<Box py={0.5} px={1.5} position={'absolute'} top={'20px'}>
				<Typography
					fontSize={'20px'}
					fontWeight={700}
					color={'#256EA6'}
					sx={{
						display         : '-webkit-box',
						WebkitBoxOrient : 'vertical',
						WebkitLineClamp : 2,
						opacity         : 0.5,
						overflow        : 'hidden',
						textOverflow    : 'ellipsis'
					}}
				>
					{watermarkTxt}
				</Typography>
			</Box>
			{!isDraft && isAdminView && (
				<Box position={'absolute'} top={'6px'} left={'0px'}>
					<Stack
						direction={'row'}
						px={1}
						py={0.2}
						spacing={0.5}
						borderRadius={'0px  16px 16px  0px'}
						bgcolor={
							isPublished || isEnabled
								? '#D2EFE1'
								: isScheduled
									? '#FFD78A'
									: isDraft || isDisabled
										? '#F6F6F6'
										: '#E3E3E3E5'
						}
						alignItems={'center'}
					>
						{isScheduled && (
							<Box width='14px' height='14px'>
								<img src={ScheduleTimeIcon} alt='' />
							</Box>
						)}
						<Typography fontSize={'12px'} color={'#212121'}>
							{statusText}
						</Typography>
					</Stack>
				</Box>
			)}
		</Box>
	);
};

export default ThumbnailView;